import { useEffect, useRef, useContext, createContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Node } from "@/data/tree";

export interface AccordionContextType {
  getExpandedId: (depth: number) => string | null;
  handleToggle: (id: string, depth: number) => void;
}

export const AccordionContext = createContext<AccordionContextType | null>(null);

interface TreeNodeProps {
  node: Node;
  depth?: number;
}

const nodeTypeStyles = {
  domain: "text-4xl md:text-5xl font-light tracking-tight text-white/90 glow-text",
  skill: "text-lg font-medium tracking-wide text-white/70 hover:text-white/90 transition-colors",
  tool: "text-sm font-normal text-white/50 hover:text-white/70 cursor-pointer",
  output: "text-sm text-white/40 hover:text-white/60 underline decoration-white/20 underline-offset-4 hover:decoration-white/40 transition-all",
};

export function TreeNode({ node, depth = 0 }: TreeNodeProps) {
  const hasChildren = node.children && node.children.length > 0;
  const expandedRef = useRef(node.id);

  const accordionContext = useContext(AccordionContext);

  const isExpanded = accordionContext?.getExpandedId(depth) === node.id;

  const handleToggle = () => {
    if (hasChildren) {
      accordionContext?.handleToggle(node.id, depth);
    }
  };

  useEffect(() => {
    const updateExpandedCount = () => {
      const expanded = document.querySelectorAll("[data-expanded='true']").length;
      window.dispatchEvent(new CustomEvent("tree-expand-change", { detail: { count: expanded } }));
    };
    updateExpandedCount();
  }, [isExpanded]);

  return (
    <div className="ml-0 md:ml-4" data-tree-id={node.id} data-expanded={hasChildren && isExpanded ? "true" : "false"}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className={cn(
          "flex items-center gap-3 py-2 cursor-pointer select-none group",
          "transition-all duration-300"
        )}
        onClick={handleToggle}
        style={{ paddingLeft: `${depth * 20}px` }}
      >
        <div
          className={cn(
            "transition-transform duration-300 ease-out text-white/30 group-hover:text-white/50",
            isExpanded && "rotate-90",
            !hasChildren && "invisible"
          )}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </div>
        
        <div className="flex flex-col gap-0.5">
          <span
            className={cn(
              nodeTypeStyles[node.type || "tool"],
              "transition-all duration-300"
            )}
          >
            {node.title}
          </span>
          {node.description && (
            <span className="text-xs text-white/30 font-light max-w-md">
              {node.description}
            </span>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {hasChildren && isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="overflow-hidden"
          >
            {node.children!.map((child, index) => (
              <motion.div
                key={child.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <TreeNode node={child} depth={depth + 1} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}